import axios, { AxiosError, type AxiosResponse } from 'axios'
import { useAuthStore } from '@/stores/auth'
import { toast } from '@/utils/toast'

let isRedirectingToLogin = false

export interface ApiResponse<T = any> {
  code: number
  message: string
  data: T
  timestamp: number
}

const request = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 30000
})

request.interceptors.request.use(
  (config) => {
    const auth = useAuthStore()
    if (auth.token) {
      config.headers.Authorization = `Bearer ${auth.token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

request.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    // raw 直通：非 R<> 包装的接口（如 zlm WebRTC SDP 交换，应答为 {code:0,sdp}），
    // 原样返回响应体，不走 code!==200 的 R 包装判定
    if ((response.config as { raw?: boolean }).raw) {
      return response.data
    }
    const res = response.data
    if (res.code !== 200) {
      return Promise.reject(new Error(res.message || '请求失败'))
    }
    return res.data
  },
  (error: AxiosError<ApiResponse>) => {
    const status = error.response?.status
    const message = error.response?.data?.message || error.message || '网络错误'
    if (status === 401) {
      if (!isRedirectingToLogin) {
        isRedirectingToLogin = true
        toast.error('登录已过期，请重新登录')
        const auth = useAuthStore()
        auth.logout()
        import('@/router').then((m) => m.default.replace('/login')).finally(() => {
          isRedirectingToLogin = false
        })
      }
      return Promise.reject(new Error('登录已过期'))
    }
    return Promise.reject(new Error(message))
  }
)

export default request
